import { useState, useEffect } from 'react'
import { HiPencil, HiExclamationTriangle } from 'react-icons/hi2'
import { Input, Select, Button, SectorSelect } from '../../../components'
import type { SectorOption } from '@/components/Select/SectorSelect'
import { BaseModal } from '../../../components/Modal/BaseModal'
import type { User, Setor } from '../../../models'
import type { UpdateUserRequest } from '../types'

interface EditUserModalProps {
  isOpen: boolean
  user: User | null
  setores: Setor[]
  onClose: () => void
  onSubmit: (userId: number, data: UpdateUserRequest) => Promise<boolean>
}

const FUNCAO_OPTIONS = [
  { value: 'USUARIO', label: 'Usuário' },
  { value: 'DIRETOR', label: 'Diretor' },
  { value: 'ADMIN', label: 'Administrador' },
]

export const EditUserModal = ({ isOpen, user, setores, onClose, onSubmit }: EditUserModalProps) => {
  const [formData, setFormData] = useState<UpdateUserRequest>({})
  const [errors, setErrors] = useState<Partial<Record<keyof UpdateUserRequest, string>>>({})
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [showFuncaoWarning, setShowFuncaoWarning] = useState(false)

  // Initialize form when user changes
  useEffect(() => {
    if (user && isOpen) {
      setFormData({
        name: user.name,
        email: user.email,
        cargo_usuario: user.cargo_usuario,
        funcao_usuario: user.funcao_usuario as 'USUARIO' | 'DIRETOR' | 'ADMIN',
        setor_logado_fk: user.setor_logado_fk,
      })
      setErrors({})
      setShowFuncaoWarning(false)
    }
  }, [user, isOpen])

  const validateForm = (): boolean => {
    const newErrors: Partial<Record<keyof UpdateUserRequest, string>> = {}

    if (formData.name !== undefined && !formData.name.trim()) {
      newErrors.name = 'Nome não pode estar vazio'
    }

    if (formData.email !== undefined && formData.email.trim()) {
      const emailTrimmed = formData.email.trim()
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailTrimmed)) {
        newErrors.email = 'Email inválido'
      }
    }

    if (formData.cargo_usuario !== undefined && !formData.cargo_usuario.trim()) {
      newErrors.cargo_usuario = 'Cargo não pode estar vazio'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!user || !validateForm()) return

    // Only send fields that have been modified (trim all strings)
    const dataToSend: UpdateUserRequest = {}

    if (formData.name && formData.name.trim() !== user.name) {
      dataToSend.name = formData.name.trim()
    }
    if (formData.email && formData.email.trim() !== user.email) {
      dataToSend.email = formData.email.trim()
    }
    if (formData.cargo_usuario && formData.cargo_usuario.trim() !== user.cargo_usuario) {
      dataToSend.cargo_usuario = formData.cargo_usuario.trim()
    }
    if (formData.funcao_usuario && formData.funcao_usuario !== user.funcao_usuario) {
      dataToSend.funcao_usuario = formData.funcao_usuario
    }
    if (
      formData.setor_logado_fk !== user.setor_logado_fk &&
      formData.setor_logado_fk !== undefined
    ) {
      dataToSend.setor_logado_fk = formData.setor_logado_fk
    }

    // If nothing changed, just close
    if (Object.keys(dataToSend).length === 0) {
      onClose()
      return
    }

    setIsSubmitting(true)
    const success = await onSubmit(user.id, dataToSend)
    setIsSubmitting(false)

    if (success) {
      onClose()
    }
  }

  const handleChange = (field: keyof UpdateUserRequest, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }))

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }))
    }

    // Show warnings for critical changes
    if (field === 'funcao_usuario' && value !== user?.funcao_usuario) {
      setShowFuncaoWarning(true)
    } else if (field === 'funcao_usuario') {
      setShowFuncaoWarning(false)
    }
  }

  const setorOptions: SectorOption[] = setores.map(setor => ({
    value: setor.id,
    label: setor.nome_setor,
    nome_setor: setor.nome_setor
  }))

  if (!user) return null

  return (
    <BaseModal
      isOpen={isOpen}
      onClose={onClose}
      title='Editar Usuário'
      icon={<HiPencil className='w-6 h-6' />}
      description={user.name}
      size='xl'
      useStandardLayout={true}
      resizable={true}
      stickyFooter={true}
      footerContent={
        <div className='flex items-center justify-end gap-3 p-4'>
          <Button type='button' variant='ghost' onClick={onClose} disabled={isSubmitting}>
            Cancelar
          </Button>
          <Button
            type='submit'
            variant='primary'
            disabled={isSubmitting}
            className='min-w-[140px]'
            onClick={handleSubmit}
          >
            {isSubmitting ? (
              <div className='flex items-center gap-2'>
                <svg className='animate-spin w-4 h-4' fill='none' viewBox='0 0 24 24'>
                  <circle
                    className='opacity-25'
                    cx='12'
                    cy='12'
                    r='10'
                    stroke='currentColor'
                    strokeWidth='4'
                  ></circle>
                  <path
                    className='opacity-75'
                    fill='currentColor'
                    d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z'
                  ></path>
                </svg>
                Salvando...
              </div>
            ) : (
              'Salvar Alterações'
            )}
          </Button>
        </div>
      }
    >
      <form onSubmit={handleSubmit} className='p-4 space-y-6'>
        <div className='grid grid-cols-1 md:grid-cols-2 gap-6'>
          {/* Nome */}
          <div className='md:col-span-2'>
            <label className='block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2'>
              Nome Completo
            </label>
            <Input
              value={formData.name || ''}
              onChange={e => handleChange('name', e.target.value)}
              placeholder='Ex: João da Silva'
              disabled={isSubmitting}
              className={errors.name ? 'border-red-500' : ''}
            />
            {errors.name && (
              <p className='mt-1 text-sm text-red-600 dark:text-red-400'>{errors.name}</p>
            )}
          </div>

          {/* Email */}
          <div className='md:col-span-2'>
            <label className='block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2'>
              Email Corporativo
            </label>
            <Input
              type='email'
              value={formData.email || ''}
              onChange={e => handleChange('email', e.target.value)}
              placeholder='nome@exemplo.com'
              disabled={isSubmitting}
              className={errors.email ? 'border-red-500' : ''}
            />
            {errors.email && (
              <p className='mt-1 text-sm text-red-600 dark:text-red-400'>{errors.email}</p>
            )}
            <p className='mt-1 text-xs text-gray-500 dark:text-gray-400'>
              O login é feito via SSO (Azure AD)
            </p>
          </div>

          {/* Cargo */}
          <div>
            <label className='block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2'>
              Cargo
            </label>
            <Input
              value={formData.cargo_usuario || ''}
              onChange={e => handleChange('cargo_usuario', e.target.value)}
              placeholder='Ex: Analista de Sistemas'
              disabled={isSubmitting}
              className={errors.cargo_usuario ? 'border-red-500' : ''}
            />
            {errors.cargo_usuario && (
              <p className='mt-1 text-sm text-red-600 dark:text-red-400'>{errors.cargo_usuario}</p>
            )}
          </div>

          {/* Função */}
          <div>
            <label className='block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2'>
              Função
            </label>
            <Select
              options={FUNCAO_OPTIONS}
              value={formData.funcao_usuario || user.funcao_usuario}
              onChange={value => handleChange('funcao_usuario', value)}
              disabled={isSubmitting}
            />
            {showFuncaoWarning && (
              <div className='mt-2 flex items-start gap-2 text-yellow-600 dark:text-yellow-400'>
                <HiExclamationTriangle className='w-4 h-4 mt-0.5 flex-shrink-0' />
                <p className='text-xs'>
                  Alterar a função pode modificar as permissões do usuário no sistema.
                </p>
              </div>
            )}
          </div>

          {/* Setor Logado */}
          <div className='md:col-span-2'>
            <label className='block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2'>
              Setor Logado
            </label>
            <SectorSelect
              options={setorOptions}
              value={formData.setor_logado_fk || ''}
              onChange={(value) =>
                handleChange('setor_logado_fk', typeof value === 'number' ? value : parseInt(value as string))
              }
              placeholder='Nenhum setor selecionado'
              disabled={isSubmitting}
            />
            <p className='mt-1 text-xs text-gray-500 dark:text-gray-400'>
              Setor atual do usuário no sistema.
            </p>
          </div>
        </div>

        {/* Current Sectors Info */}
        {user.setores && user.setores.length > 0 && (
          <div className='mt-6 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4'>
            <h4 className='text-sm font-medium text-gray-700 dark:text-gray-300 mb-2'>
              Setores Vinculados ({user.setores.length})
            </h4>
            <div className='flex flex-wrap gap-2'>
              {user.setores.map(setor => (
                <span
                  key={setor.id}
                  className='inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400'
                >
                  {setor.nome_setor}
                </span>
              ))}
            </div>
            <p className='mt-2 text-xs text-gray-500 dark:text-gray-400'>
              Para gerenciar os setores vinculados, use a funcionalidade &quot;Gerenciar
              Setores&quot; após salvar.
            </p>
          </div>
        )}
      </form>
    </BaseModal>
  )
}
